import { Text } from "@hackthenorth/north";
import { Bars3Icon } from "@heroicons/react/24/outline";
import React from "react";
import styled from "styled-components";

import FloatingMobileNavMenu from "src/shared/components/FloatingMobileNavMenu";
import { useDeviceSize } from "src/shared/hooks";
import { mediaQueries } from "src/theme/deviceBreakpoints";

interface HeaderProps extends React.ComponentPropsWithoutRef<"div"> {
  title?: string;
  subtitle?: React.ReactNode;
  actionButton?: React.ReactNode;
  short?: boolean;
  mobileLayout?: "standard" | "compact" | "stacked";
}

export const TOPNAVBAR_HEIGHT = 120;

const Container = styled.div<{ $short: boolean }>`
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  grid-template-areas: "heading action";
  align-items: center;
  gap: 16px 24px;
  width: 100%;
  min-width: 0;
  height: auto;
  min-height: ${({ $short }) => ($short ? 0 : TOPNAVBAR_HEIGHT)}px;
  flex-shrink: 0;
  padding: 24px 48px;
  overflow: visible;

  /* Keep the menu above page content and below dialogs. */
  z-index: 80;
  isolation: isolate;
  background: ${({ theme }) => theme.globalConstants.color.borderLight};
  border-bottom: 1px solid
    ${({ theme }) => theme.globalConstants.color.borderGray};

  ${mediaQueries.medium} {
    padding: 24px;
  }

  ${mediaQueries.tablet} {
    grid-template-columns: 44px minmax(0, 1fr);
    grid-template-areas: "menu heading" "action action";
    gap: 12px;
    min-height: 80px;
    padding: 20px 24px;

    &[data-mobile-layout="compact"],
    &[data-mobile-layout="stacked"] {
      grid-template-areas: "menu action";
      padding: 18px 24px;
    }

    &[data-mobile-layout="compact"] [data-header-heading] {
      display: none;
    }

    &[data-mobile-layout="stacked"] {
      grid-template-areas: "menu action" "heading heading";
      row-gap: 24px;
    }

    &[data-mobile-layout="compact"] [data-header-action],
    &[data-mobile-layout="stacked"] [data-header-action] {
      justify-self: end;
    }
  }

  ${mediaQueries.smallMobile} {
    padding-left: 16px;
    padding-right: 16px;
  }
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  grid-area: heading;
  min-width: 0;
  overflow-wrap: anywhere;
`;

const MenuWrap = styled.div`
  grid-area: menu;
  position: relative;
  z-index: 2;
  align-self: start;
`;

const MenuIcon = styled(Bars3Icon)`
  display: block;
  box-sizing: content-box;
  width: 24px;
  height: 24px;
  padding: 10px;
  cursor: pointer;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
`;

const ActionContainer = styled.div`
  grid-area: action;
  min-width: 0;
  max-width: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;

  > * {
    max-width: 100%;
  }
`;

const Title = styled(Text)`
  font-size: 24px !important;
  font-weight: 900 !important;
  line-height: 1.3 !important;
  color: ${({ theme }) =>
    theme.globalConstants.color.borderGrayDark} !important;
`;

const SubTitle = styled(Text)`
  margin-top: 8px;
  font-weight: 500 !important;
  font-size: 16px !important;
  line-height: 22px !important;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary} !important;
`;

const Header: React.FC<HeaderProps> = ({
  title,
  subtitle,
  actionButton,
  short = false,
  mobileLayout = "standard",
  ...rest
}) => {
  const isTabletOrSmaller = useDeviceSize("tablet");

  return (
    <Container {...rest} $short={short} data-mobile-layout={mobileLayout}>
      {isTabletOrSmaller && (
        <MenuWrap>
          <FloatingMobileNavMenu>
            <MenuIcon aria-label="Open navigation" />
          </FloatingMobileNavMenu>
        </MenuWrap>
      )}
      <TextContainer data-header-heading>
        {title && <Title>{title}</Title>}
        {subtitle && <SubTitle>{subtitle}</SubTitle>}
      </TextContainer>
      {actionButton && (
        <ActionContainer data-header-action>{actionButton}</ActionContainer>
      )}
    </Container>
  );
};

export default Header;
